import { createOpencodeClient } from "@opencode-ai/sdk/v2/client"
import type { ServerConnection } from "@/context/server"
import { decode64 } from "@/utils/base64"

export function authTokenFromCredentials(input: { username?: string; password: string }) {
  return btoa(`${input.username ?? "opencode"}:${input.password}`)
}

// Wallarm: the chat-adapter backend authenticates session requests with the
// `wsess` cookie (rides automatically, scoped to .wallarm.com) PLUS a CSRF/XSS
// token sent as `X-Wallarm-XSS-Token`. The token is fetched once at startup
// (see entry.tsx, which requests it from the Wallarm API like the main console
// does) and stored here so every SDK client adds the header. Undefined in local
// dev / non-Wallarm deploys, where the header is simply omitted.
let wallarmXssToken: string | undefined
export function setWallarmXssToken(token: string | undefined): void {
  wallarmXssToken = token
}

// Wallarm: the active client id (the tenant that owns the run/session being
// viewed). agentic-ui runs on its own host with no apigw to inject it, so the
// embedder (platform-ui) passes it via the iframe URL (`?client_id=`); entry.tsx
// reads it and stores it here so every SDK client sends `X-Wallarm-Client-Id`.
// chat-adapter authorizes it against /v1/objects/client before scoping the
// session lookup, so a partner/global session can open a managed client's run
// instead of 404ing. Undefined in local dev → header omitted.
let wallarmClientId: string | undefined
export function setWallarmClientId(clientId: string | undefined): void {
  wallarmClientId = clientId
}

export function authFromToken(token: string | null) {
  const decoded = decode64(token ?? undefined)
  if (!decoded) return
  const separator = decoded.indexOf(":")
  if (separator === -1) return
  return {
    username: decoded.slice(0, separator) || "opencode",
    password: decoded.slice(separator + 1),
  }
}

export function createSdkForServer({
  server,
  ...config
}: Omit<NonNullable<Parameters<typeof createOpencodeClient>[0]>, "baseUrl"> & {
  server: ServerConnection.HttpBase
}) {
  const auth = (() => {
    if (!server.password) return
    return {
      Authorization: `Basic ${authTokenFromCredentials({ username: server.username, password: server.password })}`,
    }
  })()

  return createOpencodeClient({
    ...config,
    headers: {
      ...(config.headers instanceof Headers ? Object.fromEntries(config.headers.entries()) : config.headers),
      ...auth,
      ...(wallarmXssToken ? { "X-Wallarm-XSS-Token": wallarmXssToken } : {}),
      ...(wallarmClientId ? { "X-Wallarm-Client-Id": wallarmClientId } : {}),
    },
    baseUrl: server.url,
  })
}
